import { ElementType } from 'react';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { Box } from 'components/Box';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import { Icon } from 'components/Icon/Icon';
import { Initials } from 'components/Initials/Initials';
import { colors } from 'theme/theme';
import { getRgbaString } from 'utils/style';

type Props = {
  image: ApiFilestackImageData | null | undefined;
  format: 'logo' | 'banner' | 'avatar' | undefined;
  altText: string | undefined;
  previewCrop: boolean | undefined;
  previewCropToCircle: boolean | undefined;
  previewWidth?: number;
  previewHeight?: number;
  defaultImage: ElementType | undefined;
  insecureSVG: boolean | undefined;
  overlayButton: boolean | undefined;
  dragActive: boolean;
  qaId: string;
};

export function ImagePickerPreview({
  image,
  format,
  altText,
  previewCrop,
  previewCropToCircle,
  previewWidth,
  previewHeight,
  defaultImage,
  insecureSVG,
  overlayButton,
  dragActive,
  qaId,
}: Props) {
  const isBannerWithOverlay = format === 'banner' && overlayButton;

  if (format === 'avatar') {
    if (image) {
      return (
        <FilestackImage
          data-qa-id={`${qaId}-uploaded-image`}
          width={previewWidth}
          height={previewHeight}
          data={image}
          alt={altText || getText('Placeholder')}
          insecureSVG={insecureSVG}
          round={previewCropToCircle}
          objectFit="cover"
        />
      );
    }

    return (
      <Initials
        name={altText || 'P'}
        size={previewWidth || 128}
        round={previewCropToCircle}
        backgroundColorName="elementGrey"
      />
    );
  }

  if (format === 'logo') {
    if (image) {
      return (
        <FilestackImage
          data-qa-id={`${qaId}-uploaded-image`}
          width={previewWidth}
          data={image}
          alt={altText || getText('Placeholder')}
          insecureSVG={insecureSVG}
          noCrop
        />
      );
    }

    return <Initials name={altText || 'P'} size={previewWidth || 128} />;
  }

  if (image) {
    return (
      <Box
        height={isBannerWithOverlay ? [80, null, null, 200] : undefined}
        opacity={dragActive ? 0.5 : undefined}
      >
        <FilestackImage
          data-qa-id={`${qaId}-uploaded-image`}
          width={previewWidth}
          height={isBannerWithOverlay ? 200 : previewHeight}
          alt={altText || getText('Preview image')}
          noCrop={!previewCrop}
          round={previewCropToCircle}
          data={image}
          expandSize={isBannerWithOverlay}
          objectFit={isBannerWithOverlay ? 'cover' : 'contain'}
          insecureSVG={insecureSVG}
        />
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      borderRadius={!isBannerWithOverlay ? '8px' : undefined}
      bg={
        dragActive && isBannerWithOverlay
          ? `rgba(${getRgbaString(colors.skyBlue, 0.2)})`
          : colors.inputGrey
      }
      height={
        format === 'banner'
          ? previewHeight || [80, null, null, 200]
          : previewHeight
      }
      width={format === 'banner' ? '100%' : previewWidth}
      overflow="hidden"
    >
      {!overlayButton &&
        (defaultImage ? (
          <Box component={defaultImage} maxWidth="100%" height="auto" />
        ) : (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            pointerEvents="none"
          >
            <Icon name="mountain" size={45} colorName="selectionGrey" />
          </Box>
        ))}
    </Box>
  );
}
